/**
 * IO Services — the customer-facing marketplace where studios can
 * browse and request services InteriorIO itself provides (floor
 * protection, pest control, website landing pages, etc.).
 *
 * Page structure:
 *   .projects-header — H1 "IO Services" + subtitle + "My requests" link
 *   .filter-bar      — All / per-category chips + local search
 *   Category sections — grouped service cards under each category
 *     blurb. Cards show title, short description, price label,
 *     features, "Request" CTA.
 *   Empty state when admin hasn't seeded the catalogue yet.
 *
 * Clicking "Request" opens an order modal that pre-fills contact
 * info from the current user + studio doc, lets the studio pick an
 * optional project, and writes a `platformServiceOrders` doc on
 * submit. Status starts at 'new' — the platform admin sees it in
 * `/admin/io-orders` and contacts the studio out of band.
 */
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useFullOrganization } from '@/lib/useFullOrganization';
import {
  PLATFORM_SERVICE_CATEGORIES,
  getPlatformServiceCategoryConfig,
  type PlatformService,
  type PlatformServiceCategory,
} from '@/lib/platformServices';
import { createPlatformServiceOrder } from '@/lib/platformServiceOrders';
import { usePlatformServices } from '@/lib/usePlatformServices';
import { useProjects } from '@/lib/useProjects';

export function IOServicesPage() {
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const [localSearch, setLocalSearch] = useState('');
  const [category, setCategory] = useState<PlatformServiceCategory | null>(null);
  const [orderFor, setOrderFor] = useState<PlatformService | null>(null);

  useEffect(() => {
    setPlaceholder('Search IO services…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const effectiveSearch = (searchQuery || localSearch).trim();
  const { data, loading } = usePlatformServices({
    category,
    searchText: effectiveSearch,
  });

  const grouped = useMemo(() => {
    const map = new Map<PlatformServiceCategory, PlatformService[]>();
    for (const s of data) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }
    return map;
  }, [data]);

  return (
    <div className="page active" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>IO Services</h1>
          <div className="projects-stats">
            Done-for-you services from the InteriorIO team
          </div>
        </div>
        <NavLink to="/io-services/orders" className="btn-secondary">
          <i className="ph ph-receipt" style={{ fontSize: 14 }} /> My requests
        </NavLink>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <button
          type="button"
          className={`filter-chip${category === null ? ' active' : ''}`}
          onClick={() => setCategory(null)}
        >
          All
        </button>
        {PLATFORM_SERVICE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${category === c.key ? ' active' : ''}`}
            onClick={() => setCategory(c.key)}
            title={c.label}
          >
            <i className={c.icon} /> {c.label}
          </button>
        ))}
        <div className="filter-sep" />
        <div className="filter-search-wrap" style={{ flex: 1 }}>
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="search"
            placeholder="Search services…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, color: 'var(--text-secondary)' }}>
          Loading services…
        </div>
      ) : data.length === 0 ? (
        <EmptyState category={category} hasSearch={Boolean(effectiveSearch)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {PLATFORM_SERVICE_CATEGORIES.map((c) => {
            const list = grouped.get(c.key);
            if (!list || list.length === 0) return null;
            return (
              <CategorySection
                key={c.key}
                category={c.key}
                services={list}
                onRequest={setOrderFor}
              />
            );
          })}
        </div>
      )}

      {orderFor ? (
        <OrderModal service={orderFor} onClose={() => setOrderFor(null)} />
      ) : null}
    </div>
  );
}

// ───── Empty state ────────────────────────────────────────────

function EmptyState({
  category,
  hasSearch,
}: {
  category: PlatformServiceCategory | null;
  hasSearch: boolean;
}) {
  if (hasSearch || category) {
    return (
      <div
        style={{
          padding: 48,
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        <i
          className="ph ph-magnifying-glass"
          style={{ fontSize: 36, opacity: 0.5 }}
        />
        <div style={{ marginTop: 12 }}>
          No services match your filters.
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: 48,
        textAlign: 'center',
        color: 'var(--text-secondary)',
      }}
    >
      <i className="ph ph-storefront" style={{ fontSize: 48, opacity: 0.5 }} />
      <div style={{ marginTop: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
        IO Services coming soon
      </div>
      <div style={{ marginTop: 4, fontSize: 14 }}>
        We're putting together a curated set of done-for-you services
        for your studio. Check back shortly.
      </div>
    </div>
  );
}

// ───── Category section + card grid ───────────────────────────

function CategorySection({
  category,
  services,
  onRequest,
}: {
  category: PlatformServiceCategory;
  services: PlatformService[];
  onRequest: (s: PlatformService) => void;
}) {
  const cfg = getPlatformServiceCategoryConfig(category);
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          <i className={cfg.icon} style={{ marginRight: 8, color: cfg.color }} />
          {cfg.label}
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {cfg.blurb}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {services.map((s) => (
          <ServiceCard key={s.id} service={s} onRequest={() => onRequest(s)} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({
  service,
  onRequest,
}: {
  service: PlatformService;
  onRequest: () => void;
}) {
  const cfg = getPlatformServiceCategoryConfig(service.category);
  const heroUri = service.images?.[0] ?? service.imageUrl;
  return (
    <div
      onClick={onRequest}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onRequest();
      }}
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '0 8px 24px rgba(15,23,42,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'none';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Hero — image fills top, category icon fallback */}
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          background: cfg.tintBg,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {heroUri ? (
          <img
            src={heroUri}
            alt={service.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <i
            className={cfg.icon}
            style={{ fontSize: 40, color: cfg.color }}
          />
        )}
        {service.deliveryTime ? (
          <div
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              padding: '4px 9px',
              borderRadius: 8,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <i className="ph ph-clock" /> {service.deliveryTime}
          </div>
        ) : null}
      </div>
      {/* Body */}
      <div
        style={{
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 1,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text-primary)' }}>
          {service.title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {service.tagline || service.shortDescription}
        </div>
        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 8,
            paddingTop: 10,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{service.priceLabel}</div>
            {service.priceNote ? (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {service.priceNote}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRequest();
            }}
            style={{
              padding: '7px 14px',
              borderRadius: 8,
              background: '#FF3F6C',
              border: 'none',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Request
          </button>
        </div>
      </div>
    </div>
  );
}

// ───── Order modal ────────────────────────────────────────────

function OrderModal({
  service,
  onClose,
}: {
  service: PlatformService;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { userDoc } = useCurrentUser();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const orgName =
    ((org?.data as { name?: string } | undefined)?.name ?? '').trim() || 'Your Studio';
  const userData = (userDoc?.data ?? {}) as {
    displayName?: string;
    fullName?: string;
    phone?: string;
    email?: string;
  };
  const userName =
    userData.displayName?.trim() ||
    userData.fullName?.trim() ||
    user?.displayName ||
    user?.phoneNumber ||
    user?.email ||
    'You';
  const defaultPhone = (userData.phone || user?.phoneNumber || '').trim();
  const defaultEmail = (userData.email || user?.email || '').trim();

  const { projects } = useProjects();

  const [contactName, setContactName] = useState(userName);
  const [contactPhone, setContactPhone] = useState(defaultPhone);
  const [contactEmail, setContactEmail] = useState(defaultEmail);
  const [projectId, setProjectId] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId) ?? null,
    [projects, projectId],
  );

  // Sync defaults once the user / org doc resolves
  useEffect(() => {
    if (!contactName && userName) setContactName(userName);
    if (!contactPhone && defaultPhone) setContactPhone(defaultPhone);
    if (!contactEmail && defaultEmail) setContactEmail(defaultEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, defaultPhone, defaultEmail]);

  async function submit() {
    if (!user || !orgId) {
      setError('You need to be signed in with a studio to submit a request.');
      return;
    }
    if (!contactName.trim() || !contactPhone.trim()) {
      setError('Contact name and phone are required so we can reach you.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createPlatformServiceOrder({
        serviceId: service.id,
        serviceTitle: service.title,
        serviceCategory: service.category,
        servicePriceLabel: service.priceLabel,

        orgId,
        orgName,

        requestedByUid: user.uid,
        requestedByName: userName,
        requestedByPhone: defaultPhone,
        requestedByEmail: defaultEmail,

        projectId: projectId || undefined,
        projectName: selectedProject?.name,

        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim() || undefined,
        serviceAddress: serviceAddress.trim() || undefined,
        preferredDate: preferredDate || undefined,
        notes: notes.trim() || undefined,
      });
      setSubmitted(true);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Could not submit the request. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="io-order-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 201,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 560,
            maxHeight: 'calc(100vh - 32px)',
            background: 'var(--surface)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header — hero image + title block */}
          <div style={{ position: 'relative' }}>
            {(() => {
              const heroUri = service.images?.[0] ?? service.imageUrl;
              return heroUri ? (
                <img
                  src={heroUri}
                  alt={service.title}
                  style={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                  }}
                />
              ) : null;
            })()}
            <button
              type="button"
              onClick={() => !busy && onClose()}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 32,
                height: 32,
                borderRadius: 16,
                background: 'rgba(255,255,255,0.95)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 18,
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="ph ph-x" />
            </button>
            <div
              style={{
                padding: '14px 20px 10px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <h2 id="io-order-title" style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                {service.title}
              </h2>
              {service.tagline ? (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {service.tagline}
                </div>
              ) : null}
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 12,
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{service.priceLabel}</div>
                  {service.priceNote ? (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {service.priceNote}
                    </div>
                  ) : null}
                </div>
                {service.deliveryTime ? (
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        letterSpacing: 0.5,
                      }}
                    >
                      DELIVERY
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{service.deliveryTime}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Body */}
          {submitted ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  margin: '0 auto 12px',
                  borderRadius: '50%',
                  background: '#D1FAE5',
                  color: '#047857',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                }}
              >
                <i className="ph-fill ph-check-circle" />
              </div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Request received</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 8 }}>
                Our team will contact you on{' '}
                <strong>{contactPhone}</strong> within one working day.
              </p>
              <button
                type="button"
                className="btn-primary"
                onClick={onClose}
                style={{ marginTop: 16 }}
              >
                Done
              </button>
            </div>
          ) : (
            <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {service.features && service.features.length > 0 ? (
                <div
                  style={{
                    background: 'var(--surface-muted, #f8fafc)',
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      color: 'var(--text-secondary)',
                      marginBottom: 8,
                    }}
                  >
                    WHY INTERIOR OS
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {service.features.map((f) => (
                      <li
                        key={f}
                        style={{
                          fontSize: 13,
                          display: 'flex',
                          gap: 8,
                          alignItems: 'flex-start',
                        }}
                      >
                        <i
                          className="ph-fill ph-check-circle"
                          style={{ color: '#10b981', flexShrink: 0, marginTop: 2 }}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {service.longDescription ? (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  {service.longDescription}
                </div>
              ) : null}

              <Field label="Contact name *">
                <input
                  className="input"
                  type="text"
                  value={contactName}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setContactName(v);
                  }}
                />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Phone *">
                  <input
                    className="input"
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setContactPhone(v);
                    }}
                  />
                </Field>
                <Field label="Email">
                  <input
                    className="input"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setContactEmail(v);
                    }}
                  />
                </Field>
              </div>

              <Field label="Project (optional)">
                <select
                  className="input"
                  value={projectId}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setProjectId(v);
                  }}
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Service address (optional)">
                <input
                  className="input"
                  type="text"
                  placeholder="Where should we deliver the service?"
                  value={serviceAddress}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setServiceAddress(v);
                  }}
                />
              </Field>

              <Field label="Preferred date (optional)">
                <input
                  className="input"
                  type="date"
                  value={preferredDate}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setPreferredDate(v);
                  }}
                />
              </Field>

              <Field label="Notes">
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Anything we should know about your request?"
                  value={notes}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setNotes(v);
                  }}
                />
              </Field>

              {error ? (
                <div
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    background: '#FEE2E2',
                    color: '#B91C1C',
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              ) : null}
            </div>
          )}

          {/* Footer */}
          {!submitted ? (
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={submit}
                disabled={busy}
              >
                {busy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
